import { ObjectData, ObjectId } from "@novorender/data-js-api";
import { Decompress, decompress } from "fzstd";

import { BimSignedFileUrlParams, createBimSignedFileUrl } from "./bimSignedDownloadUrl";

/**
 * FAS-relative path of the scene version's full metadata dump.
 *
 * This is the Zstandard-compressed, newline-delimited JSON (JSONL) database
 * dump produced by the BIM preprocessor. It contains one record per node —
 * file / container / object / fragment — for the whole scene, and is the same
 * data that feeds the server-side `/search` index.
 *
 * It is addressed the same proven way as `index.json` and `descendants/<id>`
 * (a `?file=<path>` on the scene's signed download URL).
 */
const METADATA_FILE_PATH = "metadata.jsonl.zst";
const METADATA_BATCH_SIZE = 5_000;

/**
 * A db (or db wrapper) that can bulk-stream a whole scene's node metadata in one
 * shot. {@link ProcoreObjectDB} implements this; callers duck-type it via
 * {@link hasSceneMetadataStream} rather than importing the concrete class so the
 * `AppObjectDB` wrapper can forward the capability transparently.
 */
export interface SceneMetadataStreamer {
    streamSceneMetadata(signal?: AbortSignal): AsyncIterableIterator<ObjectData>;
}

export interface SceneMetadataBatchStreamer {
    streamSceneMetadataBatches(signal?: AbortSignal): AsyncIterableIterator<ObjectData[]>;
}

export function hasSceneMetadataStream(db: unknown): db is SceneMetadataStreamer {
    return typeof (db as Partial<SceneMetadataStreamer> | null | undefined)?.streamSceneMetadata === "function";
}

export function hasSceneMetadataBatchStream(db: unknown): db is SceneMetadataBatchStreamer {
    return (
        typeof (db as Partial<SceneMetadataBatchStreamer> | null | undefined)?.streamSceneMetadataBatches === "function"
    );
}
/**
 * One line of the metadata dump. Mirrors `ObjectMetadata` in
 * bim-scene-processing/ObjectDB/ObjectModels.cs. Null/terrain/free ids are
 * written as placeholder lines with an empty `path`, which we skip.
 */
interface SceneMetadataRecord {
    id: number;
    path: string;
    level?: number;
    type?: number;
    name?: string;
    instanceId?: string;
    properties?: [string, string][];
}

async function loadSelf(this: ObjectData): Promise<ObjectData> {
    return this;
}

async function readOnlySave(): Promise<boolean> {
    return false;
}

function parseMetadataLine(line: string): ObjectData | null {
    // Fast reject blank lines and the empty-path placeholders the writer emits
    // for null ids (`{"id":N,"path":"","level":-1,...}`) without a full parse.
    const trimmed = line.trim();
    if (trimmed.length < 2 || trimmed[0] !== "{") {
        return null;
    }

    let record: SceneMetadataRecord;
    try {
        record = JSON.parse(trimmed) as SceneMetadataRecord;
    } catch {
        return null;
    }

    if (!record.path) {
        return null;
    }

    const data: ObjectData = {
        id: record.id as ObjectId,
        name: record.name ?? "",
        path: record.path,
        type: record.type ?? 1,
        properties: record.properties ?? [],
        loadMetaData: loadSelf,
        save: readOnlySave,
    };

    return data;
}

/**
 * Stream every node in a scene version by downloading and Zstd-decompressing the
 * FAS `metadata.jsonl.zst` file once, yielding one {@link ObjectData} per real
 * node.
 *
 * This is a single bulk download + client-side decompress/parse — far cheaper
 * than paginating the `/search` endpoint node-by-node when a caller needs the
 * whole scene (e.g. the NodeClass debug panel). Both the network body and the
 * decompressed output are consumed as streams so memory stays bounded and the
 * caller can render progressively.
 */
export async function* streamProcoreSceneMetadata(
    params: BimSignedFileUrlParams,
    signal?: AbortSignal,
): AsyncIterableIterator<ObjectData> {
    for await (const batch of streamProcoreSceneMetadataBatches(params, signal)) {
        yield* batch;
    }
}

/**
 * Batched counterpart to {@link streamProcoreSceneMetadata}. Consumers that process the complete
 * dump should prefer this API to avoid one async-generator handoff per scene node.
 */
export async function* streamProcoreSceneMetadataBatches(
    params: BimSignedFileUrlParams,
    signal?: AbortSignal,
): AsyncIterableIterator<ObjectData[]> {
    const url = await createBimSignedFileUrl(params, METADATA_FILE_PATH);
    const response = await fetch(url, { signal });

    if (!response.ok) {
        throw new Error(`Failed to fetch scene metadata file "${METADATA_FILE_PATH}": ${response.status}`);
    }

    const decoder = new TextDecoder();
    // The zstd stream handler runs synchronously inside `push()`, appending
    // decoded text to this buffer; we drain complete lines after each push.
    let textBuffer = "";
    const decompressor = new Decompress((chunk, final) => {
        textBuffer += decoder.decode(chunk, { stream: !final });
    });

    function* drainLines(flush: boolean): Generator<ObjectData[]> {
        let batch: ObjectData[] = [];
        let offset = 0;
        let newlineIdx: number;
        while ((newlineIdx = textBuffer.indexOf("\n", offset)) !== -1) {
            const line = textBuffer.slice(offset, newlineIdx);
            offset = newlineIdx + 1;
            const obj = parseMetadataLine(line);
            if (obj) {
                batch.push(obj);
                if (batch.length === METADATA_BATCH_SIZE) {
                    yield batch;
                    batch = [];
                }
            }
        }
        // Slice once per decompressed chunk rather than once per JSON line. Large metadata
        // chunks can contain tens of thousands of lines, where repeatedly copying the remaining
        // string turns parsing into quadratic work.
        textBuffer = textBuffer.slice(offset);
        if (flush && textBuffer.length > 0) {
            const obj = parseMetadataLine(textBuffer);
            textBuffer = "";
            if (obj) {
                batch.push(obj);
            }
        }
        if (batch.length) {
            yield batch;
        }
    }

    const reader = response.body?.getReader();

    // Fallback for environments without a streaming body: decompress the whole
    // payload at once.
    if (!reader) {
        const compressed = new Uint8Array(await response.arrayBuffer());
        const text = new TextDecoder().decode(decompress(compressed));
        let batch: ObjectData[] = [];
        for (const line of text.split("\n")) {
            if (signal?.aborted) {
                return;
            }
            const obj = parseMetadataLine(line);
            if (obj) {
                batch.push(obj);
                if (batch.length === METADATA_BATCH_SIZE) {
                    yield batch;
                    batch = [];
                }
            }
        }
        if (batch.length) {
            yield batch;
        }
        return;
    }

    while (!signal?.aborted) {
        const { done, value } = await reader.read();
        if (done) {
            decompressor.push(new Uint8Array(0), true);
            break;
        }
        decompressor.push(value, false);
        yield* drainLines(false);
    }

    if (signal?.aborted) {
        await reader.cancel().catch(() => undefined);
        return;
    }

    // Flush any remaining decompressed text (incl. a final line without a newline).
    yield* drainLines(true);
}
